/**
 * Sentry (frontend error tracking) — init + PII scrubbing.
 *
 * Errors only: no performance tracing, no session replay. Both are opt-in in
 * @sentry/react v8, so simply not adding them leaves the default global
 * error / unhandled-rejection handlers in place while shipping nothing else.
 *
 * All outbound events pass through `beforeSend` → scrub(), stripping JWT /
 * email / phone before they leave the browser (see security.md §7 / §13 —
 * never send tokens or PII to Sentry).
 */

import * as Sentry from '@sentry/react';
import { isExpectedApiError } from './api/expectedError';
import { config } from './config';
import { registerErrorSink, scrub } from './log';

let initialised = false;

export function initSentry(): void {
  const dsn = config.telemetry.sentryDsn;
  if (!dsn || initialised) return;
  initialised = true;

  Sentry.init({
    dsn,
    // Groups every event by the code-level version in src/lib/version.ts, so
    // "this only happens on 0.4.2" is a query rather than a guess — and is what
    // a future sourcemap upload would key against.
    release: config.appVersion,
    // Filterable in the Sentry UI: `airmarket`, `sait-dev`, etc.
    environment: config.isDev ? `${config.tenant.id}-dev` : config.tenant.id,
    tracesSampleRate: 0, // errors only — no performance transactions
    sendDefaultPii: false,
    beforeSend(event, hint) {
      // Drop expected failures from the not-yet-real mock REST endpoints so
      // they never paint a false picture of app health (see api/expectedError).
      if (isExpectedApiError(hint?.originalException)) return null;

      if (event.message) event.message = scrub(event.message);
      for (const ex of event.exception?.values ?? []) {
        if (ex.value) ex.value = scrub(ex.value);
      }
      for (const bc of event.breadcrumbs ?? []) {
        if (bc.message) bc.message = scrub(bc.message);
      }
      if (event.extra) {
        for (const [k, v] of Object.entries(event.extra)) {
          if (typeof v === 'string') event.extra[k] = scrub(v);
        }
      }
      // Never ship user identity / request cookies / headers.
      event.user = undefined;
      if (event.request) {
        event.request.cookies = undefined;
        event.request.headers = undefined;
      }
      return event;
    },
  });

  Sentry.setTag('tenant', config.tenant.id);
  Sentry.setTag('dataSource', config.dataSource);
  Sentry.setTag('version', config.appVersion);

  // Route explicit log.error(...) calls into Sentry. The indirection keeps
  // @sentry/react out of log.ts (and thus out of the MQTT worker bundle).
  registerErrorSink((msg, ctx) => {
    Sentry.captureMessage(msg, { level: 'error', extra: ctx });
  });
}

/** Sentry's React error boundary — wraps the app tree in main.tsx so
 *  component-render crashes are captured, not just window-level errors. */
export const ErrorBoundary = Sentry.ErrorBoundary;
